import React from 'react';
import pluralize from 'pluralize';
import moment from 'moment';
import numToWords from 'num-to-words';
import lodashCloneDeep from 'lodash/fp/cloneDeep';
import lodashCompact from 'lodash/fp/compact';
import lodashTimes from 'lodash/times';
import lodashUniq from 'lodash/fp/uniq';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import capitalize from 'common/util/capitalize';
import typeCheck from 'common/util/typeCheck';
import makeFullName from 'common/util/makeFullName';
import makeApplyOverridesFunc from 'common/util/makeApplyOverridesFunc';
import makeResolveGetterValuesFunc from 'common/util/makeResolveGetterValuesFunc';

import {
  formatPrice,
  getDateRangeDuration,
} from 'common/formatters';

import {
  makeListingFullAddressString,
  getListingAppliances,
  getOwnerPaysForFields,
} from 'common/listingHelpers';

import {
  listingFormFields,
} from 'common/listingFormFields';

import {
  getHouseholdPets,
  getHouseholdVehicles,
  getLeaseParkingSpaces,
  calculateHouseholdDepositFees,
} from 'common/householdHelpers';

import {
  HOUSEHOLD_APPLICATION_PET_FIELDS_LIST,
  HOUSEHOLD_APPLICATION_VEHICLE_FIELDS_LIST,
} from 'common/householdConstants';

import {
  leaseTemplateDefinitions,
  getLeaseTemplates,
} from 'common/leaseDrafting/leaseTemplates';

import {
  PARTNER_TYPE_PROPERTY_MANAGER,
} from 'common/partnerConstants';

import {
  LISTING_AGREEMENT_CUSTOM,
} from 'common/listingConstants';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrExternalLink from 'client/components/SrExternalLink';
import SrLeaseDraftCheckbox from 'client/components/SrLeaseDraftHelper/SrLeaseDraftCheckbox';
import { transformConcessionToTextList } from 'common/concessionHelpers';
import { CONCESSION_DESCRIPTION_VARIATION_LEASE_DRAFT } from 'common/concessionConstants';

export const withoutCheckboxInterpolation = (string) => {
  if (!string) {
    return string;
  }
  return string
    .replace(/\{\*\}/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const SrLeaseSeeListingAgreement = ({ listing }) => (
  <SrExternalLink link={`https://sunroomrentals.com/pdf-viewer?pdfUrl=${listing.listingAgreementUrl}`}>
    <SrText font='greycliff' fontSize='14px' weight='700' color='blue300'>
      Click to see uploaded document
    </SrText>
  </SrExternalLink>
);

const makeGetPetValueFunc = (propName, index) => {
  return ({ applications }) => {
    const pet = getHouseholdPets({ householdApplications: applications }).pets[index];
    if (pet) {
      return pet[propName];
    }
    return null;
  };
};

const makeGetVehicleValueFunc = (propName, index) => {
  return ({ applications }) => {
    const vehicle = getHouseholdVehicles(applications)[index];
    if (vehicle) {
      return vehicle[propName];
    }
    return null;
  };
};

const makeGetRenterValueFunc = (propName, index) => {
  return ({ renters }) => {
    const renter = renters[index];
    if (renter) {
      return renter[propName];
    }
    return null;
  };
};

const makeGetGuarantorValueFunc = (propName, index) => {
  return ({ guarantors }) => {
    const guarantor = guarantors[index];
    if (guarantor) {
      return guarantor[propName];
    }
    return null;
  };
};

export const getRecipientsList = ({ leaseDraftInfo, leaseDraftConfiguration }) => {
  const {
    listing,
    renters,
    guarantors,
    householdListing,
  } = leaseDraftInfo;

  let partnerRecipients;
  if (listing.managementName && listing.managementEmail) {
    partnerRecipients = [{
      title: 'Landlord/Property Manager',
      role: 'Landlord1',
      name: capitalize(listing.managementName) || null,
      email: listing.managementEmail || null,
    }];
  }
  else if (listing.propertyOwnerName && listing.propertyOwnerEmail) {
    partnerRecipients = [{
      title: 'Landlord/Property Manager',
      role: 'Landlord1',
      name: capitalize(listing.propertyOwnerName) || null,
      email: listing.propertyOwnerEmail || null,
    }];
  }
  else if ((leaseDraftConfiguration?.partnerRecipients || []).length > 0) {
    partnerRecipients = leaseDraftConfiguration.partnerRecipients.map((recipient, index) => ({
      title: 'Landlord/Property Manager',
      name: recipient.name,
      email: recipient.email || null,
      role: `Landlord${index + 1}`,
    }));
  }
  else {
    partnerRecipients = [{
      title: 'Landlord/Property Manager',
      role: 'Landlord1',
      name: null,
      email: null,
    }];
  }

  const recipients = lodashCompact([
    ...partnerRecipients,
    ...(renters.map((renter, index) => ({
      title: `Client ${index + 1}`,
      role: `Renter${index + 1}`,
      name: capitalize(renter.name),
      email: renter.email,
    }))),

    ...(guarantors.map((guarantor, index) => ({
      title: `Guarantor${guarantors.length > 1 ? ` ${index + 1}` : ''}`,
      role: `Guarantor${index + 1}`,
      name: capitalize(guarantor.name),
      email: guarantor.email,
    }))),

    householdListing?.buyersAgentName && householdListing?.buyersAgentEmail && {
      title: 'Agent',
      role: 'Agent1',
      name: capitalize(householdListing.buyersAgentName),
      email: householdListing.buyersAgentEmail,
    },
  ]);

  return recipients;
};

const getTenantsInfo = ({ renters, applications }) =>
  renters
    .reduce((memo, renter) => {
      const application = applications.find(({ userId }) => userId === renter.id);
      return [
        ...memo,
        {
          name: capitalize(renter.name),
          age: application.dateOfBirth
            ? moment().diff(moment(application.dateOfBirth), 'years')
            : 'missing information',
        },
        ...(application.children || []),
      ];
    }, [])
    .sort((a, b) => b.age - a.age)
    .map(({ name, age }) => `${name} (${age})`)
    .join(', ');

const leaseDraftingFieldDefinitions = [
  {
    id: 'propertyType',
    title: 'Property Type',
    value: ({ listing }) => listing.propertyType,
    required: true,
  },
  {
    id: 'partner',
    title: 'Partner',
    value: ({ partner }) => partner.displayName,
    required: true,
  },
  {
    id: 'notesFromPartner',
    title: 'Notes from Partner',
    value: ({ householdListing }) => householdListing.partnerLeaseNotes,
    defaultValue: 'None.',
  },
  {
    id: 'documentsToInclude',
    title: 'Documents to include',
    enableClickToCopy: false,
    value: ({ applications, guarantors, listing, householdListing }) => {
      const documentsToInclude = getLeaseTemplates({
        applications,
        guarantors,
        listing,
        householdListing,
      })
        .filter(({ enabled }) => enabled);
      return (
        <SrBox paddingTop='5px'>
          {documentsToInclude.map(({ title }) => (
            <SrBox paddingBottom='7px' key={title}>
              <SrBox display='inlineBlock' paddingRight='7px'>
                <img alt='greenCheckmark' src='/images/checkmark-green-bold.svg' />
              </SrBox>
              {title}
            </SrBox>
          ))}
        </SrBox>
      );
    },
  },
  {
    id: 'documentsToRemove',
    title: 'Documents to remove',
    enableClickToCopy: false,
    value: ({ applications, guarantors, listing, householdListing }) => {
      const documentsToRemove = getLeaseTemplates({
        applications,
        guarantors,
        listing,
        householdListing,
      })
        .filter(({ enabled }) => !enabled);

      if (documentsToRemove.length === 0) {
        return 'None.';
      }
      return (
        <SrBox paddingTop='5px'>
          {documentsToRemove.map(({ title }) => (
            <SrBox paddingBottom='7px' key={title}>
              <SrBox display='inlineBlock' paddingRight='7px'>
                <img alt='redCheckmark' src='/images/checkmark-red-bold.svg' />
              </SrBox>
              {title}
            </SrBox>
          ))}
        </SrBox>
      );
    },
  },
  {
    id: 'partnerSubmittedDocs',
    title: 'Partner submitted documents',
    value: ({ listing }) => {
      if (listing.listingAgreementUrl) {
        const link = `https://sunroomrentals.com/pdf-viewer?pdfUrl=${listing.listingAgreementUrl}`;

        return (
          <SrExternalLink link={link}>
            <SrText font='greycliff' fontSize='14px' weight='700' color='blue300'>
              1 File Uploaded
            </SrText>
          </SrExternalLink>
        );
      }

      return null;
    },
    defaultValue: 'None.',
  },
  {
    id: 'owner',
    title: 'Owner, Landlord',
    machineValue: ({ landlord, listing }) => {
      if (listing.propertyOwnerName) {
        return capitalize(listing.propertyOwnerName);
      }
      if (landlord) {
        return capitalize(landlord.name);
      }
      return null;
    },
    value: ({ landlord, listing }) => {
      if (listing.propertyOwnerName) {
        return capitalize(listing.propertyOwnerName);
      }
      if (landlord) {
        return capitalize(landlord.name);
      }
      if (listing.listingAgreementType === LISTING_AGREEMENT_CUSTOM) {
        return <SrLeaseSeeListingAgreement listing={listing} />;
      }
    },
    required: true,
  },
  {
    id: 'tenants',
    title: 'Tenant(s)',
    value: ({ renters }) => renters.map(({ name }) => capitalize(name)).join(', '),
    required: true,
  },
  {
    id: 'address',
    title: 'Address',
    value: ({ listing }) => makeListingFullAddressString(listing),
    required: true,
  },
  {
    id: 'addressUnitNumber',
    title: 'Unit Number',
    value: ({ listing }) => listing.apartmentUnit || listing.addressUnitNumber,
    defaultValue: 'N/A',
    hideIfEmpty: true,
  },
  {
    id: 'legalDescription',
    title: 'Legal Description of Property',
    machineValue: ({ listing }) => listing.legalDescription,
    value: ({ listing, listingLinks }) => {
      const mlsListingLink = listingLinks?.find(({ siteName }) => siteName === 'MLS');

      return listing.legalDescription || (mlsListingLink
        ? (
          <SrExternalLink link={mlsListingLink.linkUrl}>
            <SrText font='greycliff' fontSize='14px' weight='700' color='blue300'>
              Check MLS
            </SrText>
          </SrExternalLink>
        )
        : null
      );
    },
    required: true,
  },
  {
    id: 'county',
    title: 'County',
    value: ({ listing }) => listing.addressCounty,
    required: true,
  },
  {
    id: 'appliances',
    title: 'Non-real-property items',
    value: ({ listing }) => getListingAppliances(listing)
      .filter(({ enabled, forLeaseHelper }) => enabled && forLeaseHelper)
      .map(({ label }) => label)
      .join(', ') || 'None',
  },
  {
    id: 'leaseTerm',
    title: 'Lease Term',
    value: ({ leadRenterId, applications }) => {
      const leaseTerm = applications.find(({ userId }) => userId === leadRenterId)?.leaseTerm;
      return leaseTerm
        ? pluralize('Month', leaseTerm, true)
        : null;
    },
    required: true,
  },
  {
    id: 'commencementDate',
    title: 'Commencement Date',
    value: ({ householdMoveInDate }) => householdMoveInDate
      ? momentWithAustinTimezone(householdMoveInDate).format('MM/DD/YYYY')
      : null,
    required: true,
  },
  {
    id: 'expirationDate',
    title: 'Expiration Date',
    value: ({ applications, leadRenterId, householdMoveInDate }) => {
      const leaseTerm = applications.find(({ userId }) => userId === leadRenterId)?.leaseTerm;
      if (!leaseTerm || !householdMoveInDate) {
        return null;
      }
      const expirationDate = momentWithAustinTimezone(householdMoveInDate)
        .subtract(1, 'day')
        .add(leaseTerm, 'months')
        .format('MM/DD/YYYY');

      return expirationDate;
    },
    required: true,
  },
  {
    id: 'leaseRenewalNoticeTimeRange',
    title: 'Lease renews month-to-month unless notice is given',
    value: '{*} 60',
    hideWhenToggled: true,
    required: true,
  },
  {
    id: 'leaseRenewalNoticeDate',
    title: 'Lease continues to renew month-to-month unless notice is given...',
    value: '{*} (1) on the last day of the month following the month in which the notice is given.',
    hideWhenToggled: true,
    required: true,
  },
  {
    id: 'rent',
    title: 'Monthly Rent',
    value: ({ householdListing, listing }) => (householdListing.rentPrice || listing.price).toFixed(2),
    required: true,
  },
  {
    id: 'firstFullMonthDueDate',
    title: 'The first full month\'s rent is due and payable not later than',
    value: ({ householdMoveInDate }) => householdMoveInDate
      ? momentWithAustinTimezone(householdMoveInDate).format('MM/DD/YYYY')
      : null,
    required: true,
  },
  {
    id: 'collectionMethod',
    title: 'Collection Method',
    value: '{*} electronic payment, {*} other means acceptable to Landlord',
    hideWhenToggled: true,
  },
  {
    id: 'tenantWillPayRentOn',
    title: 'Tenant will pay the monthly rent',
    value: '{*} (1) the first day of each month during this lease',
    hideWhenToggled: true,
  },
  {
    id: 'proratedRateOn',
    title: 'Prorated Rent on or Before',
    value: ({ householdMoveInDate }) => {
      if (!householdMoveInDate) {
        return null;
      }

      const nextMonthMoveInDate = momentWithAustinTimezone(householdMoveInDate)
        .add(1, 'month');

      return nextMonthMoveInDate.date() === 1
        ? null
        : nextMonthMoveInDate.date(1).format('MM/DD/YYYY');
    },
    defaultValue: '--',
  },
  {
    id: 'proratedRentPrice',
    title: 'Tenant will pay Landlord',
    value: ({ listing, householdMoveInDate, householdListing }) => {
      if (!householdMoveInDate) {
        return null;
      }
      const daysInStartingMonth = momentWithAustinTimezone(householdMoveInDate).daysInMonth();
      const startDateMoment = momentWithAustinTimezone(householdMoveInDate);
      const startOfNextMonthMoment = momentWithAustinTimezone(householdMoveInDate)
        .add(1, 'month')
        .date(1);

      const daysUntilStartOfNextMonth = startOfNextMonthMoment.diff(startDateMoment, 'days');
      if (daysUntilStartOfNextMonth === daysInStartingMonth) {
        return null;
      }
      const rentPrice = (householdListing.rentPrice || listing.price);
      return (rentPrice * (daysUntilStartOfNextMonth / daysInStartingMonth)).toFixed(2);
    },
    defaultValue: '--',
  },
  {
    id: 'proratedRentPlaceOfPayment',
    title: 'Place of Payment (Name)',
    value: ({ landlord, listing }) => {
      if (listing.propertyOwnerName) {
        return capitalize(listing.propertyOwnerName);
      }
      if (landlord) {
        return capitalize(landlord.name);
      }

      return listing.whoCollectsRentPayment;
    },
    required: true,
  },
  {
    id: 'proratedRentPlaceOfAddress',
    title: 'Place of Payment (Address)',
    value: ({ listing }) => listing.rentPaymentDescription,
    required: true,
  },
  {
    id: 'methodOfPayment',
    title: 'Method of Payment (3)',
    value: '{*} electronic payment, {*} other means acceptable to Landlord, {*} Landlord',
    hideWhenToggled: true,
  },
  {
    id: 'lateChargeDay',
    title: 'If Landlord does not actually receive a rent payment by',
    value: '3rd',
    hideWhenToggled: true,
    required: true,
  },
  {
    id: 'lateChargePrice',
    title: 'Initial late charge',
    value: '{*} 50.00',
    hideWhenToggled: true,
    required: true,
  },
  {
    id: 'lateChargeAdditionalDay',
    title: 'Additional late charge',
    value: '10.00',
    hideWhenToggled: true,
    required: true,
  },
  {
    id: 'returnedPaymentFee',
    title: 'Tenant will pay landlord',
    value: '50.00',
    hideWhenToggled: true,
    required: true,
  },
  {
    id: 'petInitialViolationFee',
    title: 'Initial pet violation fee',
    value: '100',
    hideWhenToggled: true,
    required: true,
  },
  {
    id: 'petDailyViolationFee',
    title: 'Daily pet violation fee',
    value: '25.00',
    hideWhenToggled: true,
    required: true,
  },
  {
    id: 'securityDeposit',
    title: 'Security deposit amount',
    value: ({ listing, householdListing, applications }) => {
      const householdDepositFees = calculateHouseholdDepositFees({
        listing,
        householdListing,
        pets: getHouseholdPets({ householdApplications: applications }).nonEsaPets,
      });
      return householdDepositFees.securityDeposit.toFixed(2);
    },
    required: true,
  },
  {
    id: 'securityDepositMethod',
    title: 'Tenant will pay a security deposit to Landlord by',
    value: '{*} electronic payment',
    hideWhenToggled: true,
  },
  {
    id: 'landlordPaysFor',
    title: 'The landlord will pay for',
    value: ({ listing }) => {
      const ownerPaysForFields = getOwnerPaysForFields(listing);
      const landlordPaysForString = Object
        .keys(ownerPaysForFields)
        .filter((fieldId) => ownerPaysForFields[fieldId])
        .map((fieldId) =>
          listingFormFields.find(({ id }) => id === fieldId).title.replace('Owner Pays ', '')
        )
        .join(', ');

      return landlordPaysForString || 'None';
    },
  },
  {
    id: 'tenantNameAges',
    title: 'Tenant(s) name & age',
    value: ({ renters, applications }) => getTenantsInfo({ renters, applications }),
    required: true,
  },
  {
    id: 'noGuestslongerThan',
    title: 'E. Guests: Tenant may not permit any guests to stay longer than',
    value: '7 (SEVEN)',
    required: true,
  },
  {
    id: 'parkingSpaces',
    title: 'Tenant may not permit more than',
    value: ({ listing, applications }) => {
      const parkingSpaces = getLeaseParkingSpaces({ listing, applications });
      return `${parkingSpaces} (${numToWords(parkingSpaces).toUpperCase()})`;
    },
    required: true,
  },
  {
    id: 'landlordTripCharges',
    title: 'Trip charges',
    value: '35.00',
    hideWhenToggled: true,
    required: true,
  },
  {
    id: 'authorizedPayKeyboxDuringLast',
    title: 'Authorized to pay keybox during the last',
    value: '30',
    hideWhenToggled: true,
    required: true,
  },
  {
    id: 'withdrawKeyboxAuthorizationFee',
    title: 'Withdraw keybox authorization fee',
    value: ({ householdListing, listing }) => (householdListing.rentPrice || listing.price).toFixed(2),
    required: true,
  },
  {
    id: 'landlordMakesNoPropertyWarranties',
    title: 'A. Landlord makes no express or implied warranties as to the Unit’s or Property’s condition',
    value: 'N/A',
    hideWhenToggled: true,
  },
  {
    id: 'tenantCompleteInventoryForm',
    title: (
      'B. Tenant will complete an Inventory and Condition Form, noting any damages ' +
      'to the Unit, and deliver it to Landlord within'
    ),
    value: '2 (TWO)',
    hideWhenToggled: true,
  },
  {
    id: 'whoWaterYard',
    title: 'Tenant will water the yard at reasonable and appropriate times including but not limited to:',
    value: 'Tenant will water the lawn as needed per city ordinance to maintain a lush yard.',
    required: true,
  },
  {
    id: 'whoMaintainsYard',
    title: 'Who will maintain the yard?',
    value: ({ listing }) => (
      `{*} ${listing.policyGardeningPaid
        ? '(a) Landlord, at Landlord'
        : '(b) Tenant, at Tenant'}’s expense, will maintain the yard`
    ),
    required: true,
  },
  {
    id: 'smoking',
    title: 'Smoking (checkbox)',
    value: '{*} not permitted',
    hideWhenToggled: true,
    required: true,
  },
  {
    id: 'repairContact',
    title: 'Repair contact',
    machineValue: ({ listing }) => {
      if (listing.repairContactPhone) {
        return listing.repairContactPhone;
      }
      return null;
    },
    value: ({ listing }) => {
      if (listing.repairContactPhone) {
        return listing.repairContactPhone;
      }

      if (listing.listingAgreementType === LISTING_AGREEMENT_CUSTOM) {
        return <SrLeaseSeeListingAgreement listing={listing} />;
      }
    },
    required: true,
  },
  {
    id: 'repairSpecificItem',
    title: '(f) the following specific items or appliances:',
    value: 'None.',
  },
  {
    id: 'listOfProvisions',
    title: 'List of provisions',
    value: ({ applications }) => {
      const { pets } = getHouseholdPets({ householdApplications: applications });
      const esaPetCount = pets.filter(({ isEsa }) => isEsa).length;
      if (esaPetCount > 0) {
        return (
          `${pluralize('pet', esaPetCount, true)} ${esaPetCount === 1 ? 'is' : 'are'} ESA. ` +
          `Pet deposit & fees waived for ${esaPetCount === 1 ? 'this' : 'these'} ` +
          `${pluralize('animal', esaPetCount, false)}.`
        );
      }
      return 'N/A';
    },
  },
  {
    id: 'earlyTerminationA',
    title: '(a) At the time Landlord agrees to permit an assignee: (checkbox)',
    value: '{*} 85%',
    hideWhenToggled: true,
    required: true,
  },
  {
    id: 'earlyTerminationB',
    title: '(b) if Landlord procures the assignee, subtenant, or replacement tenant: (checkbox)',
    value: '{*} 85%',
    hideWhenToggled: true,
    required: true,
  },
  {
    id: 'addendaDocumentsToInclude',
    title: 'Documents to include',
    value: ({ applications, guarantors, listing, householdListing }) => {
      const documentsToInclude = getLeaseTemplates({
        applications,
        guarantors,
        listing,
        householdListing,
      })
        .filter(({ enabled }) => enabled)
        .filter(({ isAddenda }) => isAddenda);
      return (
        <>
          {documentsToInclude.map(({ title }) => (
            <div key={title}>
              <SrLeaseDraftCheckbox />
              {title}
            </div>
          ))}
        </>
      );
    },
  },
  {
    id: 'leadTenantName',
    title: 'Lead Tenant name',
    value: ({ leadRenterId, renters }) => {
      const leadRenter = renters.find(({ id }) => id === leadRenterId);
      return leadRenter.name;
    },
    required: true,
  },
  {
    id: 'leadTenantEmail',
    title: 'Lead Tenant email',
    value: ({ leadRenterId, renters }) => {
      const leadRenter = renters.find(({ id }) => id === leadRenterId);
      return leadRenter.email;
    },
    required: true,
  },
  {
    id: 'landlordName',
    title: 'Landlord name',
    machineValue: ({ landlord, listing }) => {
      if (listing.propertyOwnerName) {
        return capitalize(listing.propertyOwnerName);
      }
      if (landlord) {
        return capitalize(landlord.name);
      }
      return null;
    },
    value: ({ landlord, listing }) => {
      if (listing.propertyOwnerName) {
        return capitalize(listing.propertyOwnerName);
      }
      if (landlord) {
        return capitalize(landlord.name);
      }
      if (listing.listingAgreementType === LISTING_AGREEMENT_CUSTOM) {
        return <SrLeaseSeeListingAgreement listing={listing} />;
      }
    },
    required: true,
  },
  {
    id: 'landlordEmail',
    title: 'Landlord email',
    machineValue: ({ landlord, listing }) => {
      if (listing.propertyOwnerEmail) {
        return listing.propertyOwnerEmail;
      }
      if (landlord) {
        return landlord.email;
      }
      return null;
    },
    value: ({ landlord, listing }) => {
      if (listing.propertyOwnerEmail) {
        return listing.propertyOwnerEmail;
      }
      if (landlord) {
        return landlord.email;
      }
      if (listing.listingAgreementType === LISTING_AGREEMENT_CUSTOM) {
        return <SrLeaseSeeListingAgreement listing={listing} />;
      }
    },
    required: true,
  },
  {
    id: 'broker',
    title: 'Landlord\'s broker',
    value: 'Sunroom Rentals LLC',
    hideWhenToggled: true,
    required: true,
  },
  {
    id: 'brokerCheckbox',
    title: 'Landlord’s broker (checkbox)',
    value: '{*} will not act as the property manager for landlord.',
    hideWhenToggled: true,
  },
  {
    id: 'propertyManagedBy',
    title: 'Property will be managed by (checkbox)',
    value: ({ partner }) => {
      return partner.type === PARTNER_TYPE_PROPERTY_MANAGER
        ? '{*} Property Manager for Landlord.'
        : '{*} Landlord.';
    },
    hideWhenToggled: true,
  },
  {
    id: 'propertyManagerName',
    title: 'Name of property manager',
    value: ({ listing, partner }) => {
      if (listing.managementName) {
        return capitalize(listing.managementName);
      }
      if (partner.type === PARTNER_TYPE_PROPERTY_MANAGER) {
        return partner.displayName;
      }
      return null;
    },
  },
  {
    id: 'propertyManagerPhone',
    title: 'Property manager phone',
    machineValue: ({ partner, listing }) => {
      if (listing.managementPhone) {
        return listing.managementPhone;
      }
      if (partner.type === PARTNER_TYPE_PROPERTY_MANAGER) {
        return partner.phone;
      }
      return null;
    },
    value: ({ partner, listing }) => {
      if (listing.managementPhone) {
        return listing.managementPhone;
      }
      if (partner.type === PARTNER_TYPE_PROPERTY_MANAGER) {
        return partner.phone;
      }
      if (listing.listingAgreementType === LISTING_AGREEMENT_CUSTOM) {
        return <SrLeaseSeeListingAgreement listing={listing} />;
      }
      return null;
    },
  },
  {
    id: 'propertyManagerEmail',
    title: 'Property manager email',
    machineValue: ({ listing }) => {
      if (listing.managementEmail) {
        return listing.managementEmail;
      }
      if (listing.contactPartnerEmail) {
        return listing.contactPartnerEmail;
      }
      return null;
    },
    value: ({ listing }) => {
      if (listing.managementEmail) {
        return listing.managementEmail;
      }
      if (listing.contactPartnerEmail) {
        return listing.contactPartnerEmail;
      }
      if (listing.listingAgreementType === LISTING_AGREEMENT_CUSTOM) {
        return <SrLeaseSeeListingAgreement listing={listing} />;
      }
      return null;
    },
  },
  {
    id: 'petSpecialProvisions',
    title: 'Special Provisions',
    value: null,
    hideIfEmpty: true,
  },

  // API-only fields.

  // Pets
  ...lodashTimes(5, (index) => index).reduce((memo, index) => [
    ...memo,
    ...HOUSEHOLD_APPLICATION_PET_FIELDS_LIST.reduce((memo, propName) => [
      ...memo,
      {
        id: `pet${index + 1}${capitalize(propName)}`,
        value: makeGetPetValueFunc(propName, index),
        apiOnly: true,
      },
    ], []),
  ], []),
  {
    id: 'petDepositAmount',
    value: ({ listing, householdListing, applications }) => {
      const householdDepositFees = calculateHouseholdDepositFees({
        listing,
        householdListing,
        pets: getHouseholdPets({ householdApplications: applications }).nonEsaPets,
      });
      return householdDepositFees.petDeposit;
    },
    apiOnly: true,
  },
  {
    id: 'petFeesAmount',
    value: ({ listing, householdListing, applications }) => {
      const householdDepositFees = calculateHouseholdDepositFees({
        listing,
        householdListing,
        pets: getHouseholdPets({ householdApplications: applications }).nonEsaPets,
      });
      return householdDepositFees.petFees;
    },
    apiOnly: true,
  },
  {
    id: 'monthlyRentWithPetRentAmount',
    value: ({ listing, householdListing, applications }) => {
      const { nonEsaPets } = getHouseholdPets({ householdApplications: applications });
      const { firstMonthRent, monthlyPetRent } = calculateHouseholdDepositFees({
        pets: nonEsaPets,
        listing: {
          ...listing,
          collectFirstMonth: true,
        },
        householdListing,
      });
      return (firstMonthRent + monthlyPetRent).toFixed(2);
    },
  },
  {
    id: 'hasPets',
    value: ({ applications }) => {
      const { pets } = getHouseholdPets({ householdApplications: applications });
      return pets.length > 0;
    },
  },

  // Vehicles
  ...lodashTimes(5, (index) => index).reduce((memo, index) => [
    ...memo,
    ...HOUSEHOLD_APPLICATION_VEHICLE_FIELDS_LIST.reduce((memo, propName) => [
      ...memo,
      {
        id: `vehicle${index + 1}${capitalize(propName)}`,
        value: makeGetVehicleValueFunc(propName, index),
        apiOnly: true,
      },
    ], []),
  ], []),

  // Renters
  ...lodashTimes(4, (index) => index).reduce((memo, index) => [
    ...memo,
    {
      id: `renter${index + 1}Name`,
      value: makeGetRenterValueFunc('name', index),
      apiOnly: true,
    },
    {
      id: `renter${index + 1}Email`,
      value: makeGetRenterValueFunc('email', index),
      apiOnly: true,
    },
  ], []),

  // Guarantors
  ...lodashTimes(2, (index) => index).reduce((memo, index) => [
    ...memo,
    {
      id: `guarantor${index + 1}Name`,
      value: makeGetGuarantorValueFunc('name', index),
      apiOnly: true,
    },
    {
      id: `guarantor${index + 1}Email`,
      value: makeGetGuarantorValueFunc('email', index),
      apiOnly: true,
    },
  ], []),

  // Lease Template Checkboxes
  ...leaseTemplateDefinitions.map((leaseTemplateDefinition) => ({
    id: `${leaseTemplateDefinition.templateFieldId}Checkbox`,
    value: (args) => {
      const enabled = typeof leaseTemplateDefinition.enabled === 'function'
        ? leaseTemplateDefinition.enabled(args)
        : leaseTemplateDefinition.enabled;
      return enabled ? 'X' : '';
    },
    apiOnly: true,
  })),

  ...leaseTemplateDefinitions.map((leaseTemplateDefinition) => ({
    id: `${leaseTemplateDefinition.templateFieldId}Label`,
    value: () => leaseTemplateDefinition.title,
    apiOnly: true,
  })),

  // Property managed by checkboxes
  {
    id: 'propertyManagedByLandlordCheckbox',
    value: ({ partner }) => {
      return partner.type === PARTNER_TYPE_PROPERTY_MANAGER
        ? ''
        : 'X';
    },
    apiOnly: true,
  },
  {
    id: 'propertyManagedByPmCheckbox',
    value: ({ partner }) => {
      return partner.type === PARTNER_TYPE_PROPERTY_MANAGER
        ? 'X'
        : '';
    },
    apiOnly: true,
  },
  {
    id: 'whoMaintainsYardTenantCheckbox',
    value: ({ listing }) => listing.policyGardeningPaid ? '' : 'X',
    apiOnly: true,
  },
  {
    id: 'whoMaintainsYardLandlordCheckbox',
    value: ({ listing }) => listing.policyGardeningPaid ? 'X' : '',
    apiOnly: true,
  },
  {
    id: 'listingMlsNumber',
    value: ({ listing }) => listing.mlsNumber,
    apiOnly: true,
  },
  {
    id: 'touredProperty',
    value: ({ householdListing }) => householdListing.inPersonTour,
    apiOnly: true,
  },
  {
    id: 'applicationFee',
    value: ({ listing }) => listing.applicationFee?.toFixed(2),
    apiOnly: true,
  },

  // Generic X field, for static checkboxes
  {
    id: 'x',
    value: 'X',
    apiOnly: true,
  },

  {
    id: 'concessionAmount',
    title: 'Concession Amount',
    value: ({ concession }) => {
      if (!concession) {
        return null;
      }

      return formatPrice(concession.amount);
    },
    hideIfEmpty: true,
  },
  {
    id: 'concessionTerms',
    title: 'Concession Terms',
    value: ({ concession, listing }) => {
      if (!concession) {
        return null;
      }

      return transformConcessionToTextList({
        concession,
        rentPrice: listing.price,
        variation: CONCESSION_DESCRIPTION_VARIATION_LEASE_DRAFT,
      }).join(' ');
    },
    hideIfEmpty: true,
  },
];

export const getLeaseDraftingFieldDefinitions = (leaseDraftConfiguration) => {
  let fields = lodashCloneDeep(leaseDraftingFieldDefinitions);

  if (leaseDraftConfiguration.fieldOverrides) {
    const applyOverrides = makeApplyOverridesFunc(leaseDraftConfiguration.fieldOverrides);
    fields = applyOverrides(fields);
  }

  return fields;
};

const getApplicationDraftingFields = ({ application, leaseDraftInfo }) => {
  typeCheck('application::NonEmptyObject', application);
  typeCheck('leaseDraftInfo::NonEmptyObject', leaseDraftInfo);

  const leadRenterApplication = leaseDraftInfo.applications.find(({ userId }) => {
    return userId === leaseDraftInfo.leaseRenterId;
  });
  const applicationFields = Object.keys(application).reduce((memo, fieldName) => {

    // Some application fields are only filled by the lead renter.
    const _application = ['moveInDate', 'leaseTerm'].includes(fieldName) && leadRenterApplication
      ? leadRenterApplication
      : application;

    if (
      !Array.isArray(_application[fieldName])
      && (
        typeof _application[fieldName] === 'number'
        || typeof _application[fieldName] === 'boolean'
        || _application[fieldName]
      )
    ) {
      const value = _application[fieldName];
      const isDateValue = value
        && typeof value === 'string'
        && value.includes('-')
        && moment(value).isValid();

      memo.push({
        id: `application${capitalize(fieldName)}`,
        value: isDateValue ? momentWithAustinTimezone(value).format('MM/DD/YYYY') : value,
        apiOnly: true,
      });

      if (application.currentRentalMoveInDate) {
        memo.push({
          id: 'applicationCurrentRentalDuration',
          value: getDateRangeDuration({
            fromDate: application.currentRentalMoveInDate,
            toDate: application.currentRentalMoveOutDate || new Date(),
          }),
          apiOnly: true,
        });
      }

      if (application.previousRentalMoveInDate && application.previousRentalMoveOutDate) {
        memo.push({
          id: 'applicationPreviousRentalDuration',
          value: getDateRangeDuration({
            fromDate: application.previousRentalMoveInDate,
            toDate: application.previousRentalMoveOutDate,
          }),
          apiOnly: true,
        });
      }

      if (application.previousJobStartDate && application.previousJobEndDate) {
        memo.push({
          id: 'applicationPreviousJobDuration',
          value: getDateRangeDuration({
            fromDate: application.previousJobStartDate,
            toDate: application.previousJobEndDate,
          }),

          apiOnly: true,
        });
      }

      if (isDateValue) {
        memo.push({
          id: `application${capitalize(fieldName)}FromNow`,
          value: momentWithAustinTimezone(value).toNow(true),
          apiOnly: true,
        });
      }
    }
    return memo;
  }, []);

  applicationFields.push({
    id: 'applicationFullName',
    value: makeFullName(application.firstName, application.lastName),
    apiOnly: true,
  });

  applicationFields.push({
    id: 'applicationAnnualIncome',
    value: !isNaN(application.monthlyIncome) && application.monthlyIncome > 0
      ? String(application.monthlyIncome * 12)
      : '',
    apiOnly: true,
  });

  applicationFields.push({
    id: 'applicationPreviousJobAnnualIncome',
    value: !isNaN(application.previousJobMonthlyIncome) && application.previousJobMonthlyIncome > 0
      ? String(application.previousJobMonthlyIncome * 12)
      : '',
    apiOnly: true,
  });

  applicationFields.push({
    id: 'applicationChildrenNameAges',
    value: (application.children || [])
      .map(({ name, age }) => `${name} (${age})`)
      .join(', '),
  });

  applicationFields.push({
    id: 'applicationPetNames',
    value: (application.pets || [])
      .map(({ name }) => name)
      .join(', '),
  });

  applicationFields.push({
    id: 'applicationHasPets',
    value: (application.pets || []).length > 0,
  });

  applicationFields.push({
    id: 'applicationPetWeights',
    value: (application.pets || [])
      .filter(({ weight }) => !!weight)
      .map(({ weight }) => {
        const weightWithSuffix = String(weight).includes('lb')
          ? weight
          : `${weight} lbs`;

        return weightWithSuffix;
      })
      .join(', '),
  });

  applicationFields.push({
    id: 'applicationPetTypes',
    value: lodashUniq((application.pets || [])
      .filter(({ type }) => !!type)
      .map(({ type }) => type))
      .join(', '),
  });

  return applicationFields;
};

export const getLeaseDraftingFields = ({ leaseDraftInfo, leaseDraftConfiguration, applicationId }) => {
  typeCheck('leaseDraftInfo::NonEmptyObject', leaseDraftInfo);
  typeCheck('leaseDraftConfiguration::NonEmptyObject', leaseDraftConfiguration);
  typeCheck('applicationId::Maybe PositiveNumber', applicationId);

  // Include static field definitions.
  let leaseDraftingFields = getLeaseDraftingFieldDefinitions(leaseDraftConfiguration)
    .map(makeResolveGetterValuesFunc(leaseDraftInfo));

  // Optionally add application specific field definitions.
  if (applicationId) {
    const application = leaseDraftInfo.applications.find(({ id }) => id === applicationId);
    const applicationFields = getApplicationDraftingFields({
      application,
      leaseDraftInfo,
    });
    leaseDraftingFields.push(...applicationFields);
  }

  // Add "Yes/No" checkbox values.
  leaseDraftingFields = leaseDraftingFields.reduce((memo, field) => {
    // Some checkbox fields have separate checkboxes for the "Yes" option and the "No" option.
    // We must pass in two separate variables containing "X"
    if (typeof field.value === 'boolean') {
      memo.push({
        ...field,
        id: `${field.id}${field.value ? 'Yes' : 'No'}`,
        value: 'X',
      });
    }
    return memo;
  }, leaseDraftingFields);

  return leaseDraftingFields;
};

export default leaseDraftingFieldDefinitions;
